import { Component, input, Output, EventEmitter, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatThread } from '../../models';

@Component({
  selector: 'app-conversation-list',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './conversation-list.html',
  styleUrls: ['./conversation-list.scss'],
})
export class ConversationListComponent {
  readonly threads = input<ChatThread[]>([]);
  readonly activeThreadId = input<string | null>(null);
  readonly currentUserId = input('');
  @Output() threadSelected = new EventEmitter<string>();

  searchText = signal('');

  /** Threads filtered by search text (matches participant names or last message). */
  filteredThreads = computed(() => {
    const q = this.searchText().toLowerCase().trim();
    const list = this.threads();

    // Group threads by participant combination (sorted user IDs as key),
    // keeping only the most recent thread per group.
    const grouped = new Map<string, ChatThread>();
    for (const t of list) {
      const key = [...t.participants.map((p) => p.userId)].sort().join(',');
      const existing = grouped.get(key);
      if (!existing || t.lastUpdated > existing.lastUpdated) {
        grouped.set(key, t);
      }
    }
    const deduped = Array.from(grouped.values());

    if (!q) return deduped;
    return deduped.filter((t) => {
      const names = t.participants.map((p) => p.displayName.toLowerCase()).join(' ');
      const preview = t.lastMessage?.content?.toLowerCase() ?? '';
      const topic = (t.topic ?? '').toLowerCase();
      return names.includes(q) || preview.includes(q) || topic.includes(q);
    });
  });

  onSearch(value: string): void {
    this.searchText.set(value);
  }

  /** Display name(s) of other participants. */
  otherNames(thread: ChatThread): string {
    return thread.participants
      .filter((p) => p.userId !== this.currentUserId())
      .map((p) => p.displayName)
      .join(', ');
  }

  /** Avatar URL of the first other participant. */
  otherAvatar(thread: ChatThread): string {
    return (
      thread.participants.find((p) => p.userId !== this.currentUserId())
        ?.avatarUrl ?? ''
    );
  }

  /** Get initials from participant display names for the avatar circle. */
  participantInitials(thread: ChatThread): string {
    const others = thread.participants.filter((p) => p.userId !== this.currentUserId());
    if (!others.length) return '?';
    return others
      .map((p) => {
        const parts = p.displayName.trim().split(/\s+/);
        const first = parts[0]?.[0] ?? '';
        const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
        return (first + last).toUpperCase();
      })
      .join(', ');
  }

  lastMessagePreview(thread: ChatThread): string {
    if (!thread.lastMessage) return '';
    const name =
      thread.lastMessage.senderId === this.currentUserId()
        ? 'You'
        : thread.lastMessage.senderDisplayName.split(' ')[0];
    const text =
      thread.lastMessage.content.length > 50
        ? thread.lastMessage.content.slice(0, 50) + '...'
        : thread.lastMessage.content;
    return `${name}: ${text}`;
  }

  timeAgo(thread: ChatThread): string {
    if (!thread.lastUpdated) return '';
    const diff = Date.now() - new Date(thread.lastUpdated).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `${days}d`;
  }

  /** Format date and time for display: DD/MM/YYYY  HH:MM AM/PM */
  formatDateTime(thread: ChatThread): string {
    if (!thread.lastUpdated) return '';
    const d = new Date(thread.lastUpdated);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
    return `${day}/${month}/${year}  ${time}`;
  }
}
